import { TFunction } from 'i18next';
import { Dispatch, SetStateAction } from 'react';
import { apiClientWithoutHandler } from 'src/js/api';
import { APIPath, APIPathWithArguments } from 'src/js/api/route-path-index';
import { NotificationVariant } from 'src/js/notification/notification';
import { notificationHandler } from 'src/js/notification/notification-handler';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { createWatchStore, updateWatchStore } from 'src/js/store/use-watch';
import { isNilOrEmpty } from 'src/js/util/global-util';

import {
  St2110Response,
  St2110ViewModel,
  GetAllSt2110sResponse,
  GetAllResolutionsResponse,
  ResolutionViewModel,
  mapSt2110ViewModel,
  mapSt2110sResponse,
  mapResolutionResponse,
} from './st2110-models';

const st2110Models: St2110ViewModel[] = [];
createWatchStore(st2110Models);
export default st2110Models;

export const resolutions: ResolutionViewModel[] = [];
createWatchStore(resolutions);

export const saveResponseToSt2110s = (response: St2110ViewModel[]) => {
  updateWatchStore(st2110Models, response);
};

const replaceViewModel = (response: St2110Response, t: TFunction, selected = false) => {
  const viewModel = mapSt2110ViewModel(response, t);
  const st2110s = [...st2110Models];
  const index = st2110s.findIndex((e) => e.id === viewModel.id);
  if (index > -1) {
    st2110s.splice(index, 1, { ...viewModel, selected: selected });
  }

  saveResponseToSt2110s(st2110s);
};

const deleteViewModel = (model: St2110ViewModel) => {
  const st2110s = [...st2110Models];
  const index = st2110s.findIndex((e) => e.id === model.id);
  if (index > -1) {
    st2110s.splice(index, 1);
  }

  saveResponseToSt2110s(st2110s);
};

export const fetchSt2110 = (t: TFunction) => {
  return apiClientWithoutHandler.genericController.get<GetAllSt2110sResponse>(APIPath.st2110.list).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        const viewModels = mapSt2110sResponse(response, t);
        saveResponseToSt2110s(viewModels);
      }
    }
    return res;
  });
};

export const fetchSt2110Resolutions = () => {
  return apiClientWithoutHandler.genericController
    .get<GetAllResolutionsResponse>(APIPath.st2110.resolutions)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          const viewModels = mapResolutionResponse(response);
          updateWatchStore(resolutions, viewModels);
        }
      }
      return res;
    });
};

export const startStopSt2110 = (
  model: St2110ViewModel,
  start: boolean,
  selected: boolean,
  displayNotification: boolean,
  t: TFunction,
) => {
  const path = APIPathWithArguments(start ? APIPath.st2110.start : APIPath.st2110.stop, {
    id: model.id,
  });
  return apiClientWithoutHandler.genericController
    .put<St2110Response>(path)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          replaceViewModel(response, t, selected);
        }
      }
      return res;
    })
    .then((res) => {
      displayNotification && notificationHandler(res, null, null, t);
      return res;
    });
};

export const fetchSt2110Statistics = (
  model: St2110ViewModel,
  t: TFunction,
  setStatistics: Dispatch<SetStateAction<St2110ViewModel>>,
) => {
  const path = APIPathWithArguments(APIPath.st2110.stats, { id: model.id });
  return apiClientWithoutHandler.genericController.get<St2110Response>(path).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        setStatistics(mapSt2110ViewModel(response, t));
      }
    }
    return res;
  });
};

export const resetSt2110Statistics = (
  model: St2110ViewModel,
  t: TFunction,
  setStatistics: Dispatch<SetStateAction<St2110ViewModel>>,
) => {
  const path = APIPathWithArguments(APIPath.st2110.stats, { id: model.id });
  return apiClientWithoutHandler.genericController.delete<St2110Response>(path).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        setStatistics(mapSt2110ViewModel(response, t));
      }
    }
    return res;
  });
};

export const createSt2110 = (model: St2110ViewModel, t: TFunction) => {
  return apiClientWithoutHandler.genericController
    .post<St2110Response>(APIPath.st2110.add, model)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          const viewModel = mapSt2110ViewModel(response, t);
          const st2110s = [...st2110Models];
          st2110s.splice(0, 0, { ...viewModel, selected: false });
          saveResponseToSt2110s(st2110s);
        }
      }
      return res;
    })
    .then((res) => {
      const response = res.data;
      if (!isNilOrEmpty(response.warning)) {
        createAndDispatchNotification(response.warning, NotificationVariant.WARNING, t);
      } else {
        notificationHandler(
          res,
          t('st2110.notifications.createSuccess'),
          response.message || t('st2110.notifications.createError'),
          t,
        );
      }
      return res;
    });
};

export const updateSt2110 = (model: St2110ViewModel, t: TFunction) => {
  const path = APIPathWithArguments(APIPath.st2110.single, { id: model.id });
  return apiClientWithoutHandler.genericController
    .put<St2110Response>(path, model)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          replaceViewModel(response, t);
        }
      }
      return res;
    })
    .then((res) => {
      notificationHandler(
        res,
        isNilOrEmpty(res.data?.info?.name)
          ? t('st2110.notifications.updateSuccessEmptyName')
          : t('st2110.notifications.updateSuccess', { name: res.data?.info?.name }),
        isNilOrEmpty(res.data?.info?.name)
          ? t('st2110.notifications.updateErrorEmptyName')
          : t('st2110.notifications.updateError', { name: res.data?.info?.name }),
        t,
      );
      return res;
    });
};

// UNUSED in decoder
export const deleteSt2110 = (model: St2110ViewModel) => {
  const path = APIPathWithArguments(APIPath.st2110.delete, { id: model.id });
  return apiClientWithoutHandler.genericController.delete(path).then((res) => {
    if (res.ok) {
      deleteViewModel(model);
    }
    return res;
  });
};
